import { Link } from "react-router-dom";
import { Linkedin, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import NewsletterSignup from "@/components/NewsletterSignup";

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-brand-cream border-t border-brand-border"
    >
      {/* Newsletter band */}
      <div className="border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <NewsletterSignup />
          </div>
          <div className="lg:col-span-5">
            <div className="border-l border-brand-olive/40 pl-6 text-sm text-brand-muted leading-relaxed font-light">
              <span className="block text-brand-deep font-medium mb-2">
                Sent only when there&rsquo;s something worth saying.
              </span>
              Brief notes on supplier movements, conference signals, and
              category shifts in specialty ingredients — written by FFIB
              analysts.
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Logo size="lg" />
            <p className="mt-6 text-brand-muted text-sm max-w-md leading-relaxed">
              IngredientSignal is a subsidiary of Food &amp; Feed Ingredients
              Biz (FFIB), focused on intelligence-led sourcing and supplier
              matchmaking for specialty ingredients.
            </p>
            <div className="mt-8 flex items-center gap-5">
              <a
                href="mailto:agastya@ingredientsignal.com"
                data-testid="footer-email-link"
                className="inline-flex items-center gap-2 text-sm text-brand-warm hover:text-brand-olive transition-colors"
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                agastya@ingredientsignal.com
              </a>
              <a
                href="https://www.linkedin.com/company/106421941/"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-linkedin-link"
                aria-label="LinkedIn"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-brand-border text-brand-warm hover:text-brand-deep hover:border-brand-muted/60 transition-colors"
              >
                <Linkedin className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-brand-olive">
              Explore
            </div>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                ["/", "Home"],
                ["/platform", "Platform"],
                ["/case-studies", "Case Studies"],
                ["/about", "About"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-brand-muted hover:text-brand-deep transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-brand-olive">
              Audiences
            </div>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                ["/for-buyers", "For Buyers"],
                ["/for-suppliers", "For Suppliers"],
                ["/get-started", "Get Started"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-brand-muted hover:text-brand-deep transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-brand-olive">
              Parent Company
            </div>
            <p className="mt-6 text-sm text-brand-muted leading-relaxed">
              Food &amp; Feed Ingredients Biz
              <br />
              <span className="text-brand-quiet">FFIB</span>
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-brand-border flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-brand-quiet tracking-wide">
            © {year} IngredientSignal — a subsidiary of FFIB. All rights reserved.
          </p>
          <p className="text-xs text-brand-quiet tracking-wide font-mono">
            Curated · Verified · Relationship-led
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
